import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  marketingApi,
  type PromotionListParams,
  type CreatePromotionInput,
  type CouponListParams,
  type CreateCouponInput,
  type BulkCreateCouponsInput,
  type FlashSaleListParams,
  type CreateFlashSaleInput,
  type ListParams,
  type CreateBundleInput,
  type BannerListParams,
  type CreateBannerInput,
  type SubscriberListParams,
  type CreateCampaignInput,
  type CreateSeasonalInput,
  type CreateStackingRuleInput,
  type TrackingLinkListParams,
  type CreateTrackingLinkInput,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Query key constants
// ---------------------------------------------------------------------------

const KEYS = {
  promotions: (params: PromotionListParams) => ["marketing-promotions", params] as const,
  coupons: (params: CouponListParams) => ["marketing-coupons", params] as const,
  flashSales: (params: FlashSaleListParams) => ["marketing-flash-sales", params] as const,
  bundles: (params: ListParams) => ["marketing-bundles", params] as const,
  banners: (params: BannerListParams) => ["marketing-banners", params] as const,
  newsletterSubscribers: (params: SubscriberListParams) => ["marketing-newsletter-subscribers", params] as const,
  newsletterCampaigns: (params: ListParams) => ["marketing-newsletter-campaigns", params] as const,
  seasonal: (params: ListParams) => ["marketing-seasonal", params] as const,
  stacking: (params: ListParams) => ["marketing-stacking", params] as const,
  tracking: (params: TrackingLinkListParams) => ["marketing-tracking", params] as const,
  analytics: (period: string) => ["marketing-analytics", period] as const,
} as const;

// ---------------------------------------------------------------------------
// Promotions
// ---------------------------------------------------------------------------

/** Fetch paginated promotions list. */
export function usePromotions(params: PromotionListParams = {}) {
  return useQuery({
    queryKey: KEYS.promotions(params),
    queryFn: () => marketingApi.promotions.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a new promotion. */
export function useCreatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePromotionInput) =>
      marketingApi.promotions.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-promotions"] });
    },
  });
}

/** Update an existing promotion. */
export function useUpdatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CreatePromotionInput> }) =>
      marketingApi.promotions.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-promotions"] });
    },
  });
}

/** Pause a promotion. */
export function usePausePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.promotions.pause(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-promotions"] });
    },
  });
}

/** Resume a paused promotion. */
export function useResumePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.promotions.resume(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-promotions"] });
    },
  });
}

/** Soft-delete a promotion. */
export function useDeletePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.promotions.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-promotions"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Coupons
// ---------------------------------------------------------------------------

/** Fetch paginated coupons list. */
export function useCoupons(params: CouponListParams = {}) {
  return useQuery({
    queryKey: KEYS.coupons(params),
    queryFn: () => marketingApi.coupons.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a single coupon. */
export function useCreateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCouponInput) => marketingApi.coupons.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-coupons"] });
    },
  });
}

/** Bulk-create coupons with a prefix. */
export function useBulkCreateCoupons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateCouponsInput) =>
      marketingApi.coupons.bulkCreate(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-coupons"] });
    },
  });
}

/** Revoke a coupon. */
export function useRevokeCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.coupons.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-coupons"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Flash Sales
// ---------------------------------------------------------------------------

/** Fetch paginated flash sales list. */
export function useFlashSales(params: FlashSaleListParams = {}) {
  return useQuery({
    queryKey: KEYS.flashSales(params),
    queryFn: () => marketingApi.flashSales.list(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a new flash sale. */
export function useCreateFlashSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFlashSaleInput) =>
      marketingApi.flashSales.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-flash-sales"] });
    },
  });
}

/** End a flash sale immediately. */
export function useEndFlashSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.flashSales.end(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-flash-sales"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Bundles
// ---------------------------------------------------------------------------

/** Fetch paginated bundles list. */
export function useBundles(params: ListParams = {}) {
  return useQuery({
    queryKey: KEYS.bundles(params),
    queryFn: () => marketingApi.bundles.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a new bundle. */
export function useCreateBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBundleInput) => marketingApi.bundles.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-bundles"] });
    },
  });
}

/** Toggle a bundle's active state. */
export function useToggleBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.bundles.toggle(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-bundles"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

/** Fetch paginated banners list. */
export function useBanners(params: BannerListParams = {}) {
  return useQuery({
    queryKey: KEYS.banners(params),
    queryFn: () => marketingApi.banners.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a new banner. */
export function useCreateBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBannerInput) => marketingApi.banners.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-banners"] });
    },
  });
}

/** Update a banner. */
export function useUpdateBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CreateBannerInput> }) =>
      marketingApi.banners.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-banners"] });
    },
  });
}

/** Toggle a banner's active state. */
export function useToggleBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.banners.toggle(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-banners"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Newsletter
// ---------------------------------------------------------------------------

/** Fetch newsletter subscribers. */
export function useNewsletterSubscribers(params: SubscriberListParams = {}) {
  return useQuery({
    queryKey: KEYS.newsletterSubscribers(params),
    queryFn: () => marketingApi.newsletter.subscribers(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Fetch newsletter campaigns. */
export function useNewsletterCampaigns(params: ListParams = {}) {
  return useQuery({
    queryKey: KEYS.newsletterCampaigns(params),
    queryFn: () => marketingApi.newsletter.campaigns(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a new newsletter campaign. */
export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCampaignInput) =>
      marketingApi.newsletter.createCampaign(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-newsletter-campaigns"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Seasonal Calendar
// ---------------------------------------------------------------------------

/** Fetch seasonal calendar events. */
export function useSeasonalEvents(params: ListParams = {}) {
  return useQuery({
    queryKey: KEYS.seasonal(params),
    queryFn: () => marketingApi.seasonal.list(params),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a seasonal calendar event. */
export function useCreateSeasonalEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSeasonalInput) =>
      marketingApi.seasonal.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-seasonal"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Stacking Rules
// ---------------------------------------------------------------------------

/** Fetch stacking rules. */
export function useStackingRules(params: ListParams = {}) {
  return useQuery({
    queryKey: KEYS.stacking(params),
    queryFn: () => marketingApi.stacking.list(params),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a new stacking rule. */
export function useCreateStackingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStackingRuleInput) =>
      marketingApi.stacking.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-stacking"] });
    },
  });
}

/** Toggle a stacking rule's active state. */
export function useToggleStackingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.stacking.toggle(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-stacking"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Tracking Links
// ---------------------------------------------------------------------------

/** Fetch tracking links. */
export function useTrackingLinks(params: TrackingLinkListParams = {}) {
  return useQuery({
    queryKey: KEYS.tracking(params),
    queryFn: () => marketingApi.tracking.list(params),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Create a new tracking link. */
export function useCreateTrackingLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTrackingLinkInput) =>
      marketingApi.tracking.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing-tracking"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

/** Fetch promo analytics summary for a period. */
export function usePromoAnalytics(period: string) {
  return useQuery({
    queryKey: KEYS.analytics(period),
    queryFn: () => marketingApi.analytics.summary(period),
    staleTime: 5 * 60_000,
  });
}
